import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';

import ChevronLeftIcon from 'components/common/icons/stroke/chevron-left-stroke';
import ChevronLeftLineStrokeIcon from 'components/common/icons/stroke/chevron-left-stroke-with-line';
import ChevronRightIcon from 'components/common/icons/stroke/chevron-right-stroke';
import ChevronRightLineStrokeIcon from 'components/common/icons/stroke/chevron-right-stroke-with-line';
import Tooltip from 'components/common/tooltip';
import DropdownMenu from 'components/common/dropdown_menu';

const calculatePaginationData = (list, pageSize, pageCount) => {
  const startIndex = (pageCount - 1) * pageSize;
  let endIndex = startIndex + pageSize - 1;
  if (!list[endIndex]) endIndex = list.length - 1;
  return {
    startIndex,
    startItem: isEmpty(list) ? 0 : startIndex + 1,
    endIndex,
    endItem: endIndex + 1,
  };
};

const options = [
  {
    label: '25',
    value: 25,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
];

/**
 * @visibleName Paginate Light
 */

const PaginateLight = ({
  list,
  currentPage,
  pageSize,
  onChange,
  showPagination,
  title,
  isLoading,
  className,
  showSizeChanger,
  onChangePageSize,
}) => {
  const showComponent = showPagination && !isLoading;
  if (!showComponent) return null;
  const { startItem, endItem } = calculatePaginationData(list, pageSize, currentPage);
  const { startItem: nextStartItem, endItem: nextEndItem } = calculatePaginationData(list, pageSize, currentPage + 1);
  const { startItem: prevStartItem, endItem: prevEndItem } = calculatePaginationData(list, pageSize, currentPage - 1);
  let lastPageNumber = Math.ceil(list.length / pageSize);
  if (isEmpty(list)) {
    lastPageNumber = 1;
  }
  const config = [
    {
      modifier: 'start',
      icon: <ChevronLeftLineStrokeIcon />,
      onUpdate: () => onChange(1),
      disabled: currentPage === 1,
      tooltipMessage: 'First Page',
    },
    {
      modifier: 'prev',
      icon: <ChevronLeftIcon />,
      onUpdate: () => onChange(--currentPage),
      disabled: currentPage === 1,
      tooltipMessage: `Prev ${prevStartItem}-${prevEndItem}`,
    },
    {
      modifier: 'next',
      icon: <ChevronRightIcon />,
      onUpdate: () => onChange(++currentPage),
      disabled: currentPage === lastPageNumber,
      tooltipMessage: `Next ${nextStartItem}-${nextEndItem}`,
    },
    {
      modifier: 'end',
      icon: <ChevronRightLineStrokeIcon />,
      onUpdate: () => onChange(lastPageNumber),
      disabled: currentPage === lastPageNumber,
      tooltipMessage: 'Last Page',
    },
  ];
  const noResultsTitle = 'No results';
  const rangeTitle = `${startItem}-${endItem} of ${list.length} ${title || 'items'}`;
  const paginationTitle = isEmpty(list) ? noResultsTitle : rangeTitle;
  return (
    <PaginateBox className={className}>
      {showSizeChanger && (
        <>
          <div>Rows per page</div>
          <DropdownMenuStyled onSelect={onChangePageSize} options={options} value={pageSize} />
        </>
      )}
      <div>{paginationTitle}</div>
      {config.map(({ icon, onUpdate, modifier, disabled, tooltipMessage }, key) => (
        <StyledTooltip
          key={key}
          margin={13}
          message={tooltipMessage}
          style={{ left: '-14px', display: disabled ? 'none' : '' }}
        >
          <PaginateItem className="pagination-item" disabled={disabled} onClick={onUpdate}>
            <PaginateItemIcon modifier={modifier}>{icon}</PaginateItemIcon>
          </PaginateItem>
        </StyledTooltip>
      ))}
    </PaginateBox>
  );
};

const DropdownMenuStyled = styled(DropdownMenu)`
  margin-right: 10px;
  margin-left: 5px;
  border-radius: 4px;
  min-height: 28px;
  max-height: 28px;
  max-width: 80px;
  & {
    padding: 8px;
  }
`;

const Disabled = css`
  pointer-events: none;
  > div {
    opacity: 0.3;
  }
`;

const PaginateItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  list-style: none;
  cursor: pointer;
  border-radius: 4px;
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  list-style: none;
  float: left;
  font-size: 12px;
  border: 1px solid #d9d9d9;
  background-color: ${p => p.theme.colors.white};
  margin-left: 8px;

  ${({ disabled }) => disabled && Disabled}
`;

const PaginateItemIcon = styled.div`
  width: 18px;
  height: 18px;
  ${({ modifier }) => modifier === 'start' && `margin-left: 5px;`}
  ${({ modifier }) => modifier === 'end' && `margin-right: 5px;`}
`;

const PaginateBox = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.black};
`;

const StyledTooltip = styled(Tooltip)`
  user-select: none;
`;

PaginateLight.propTypes = {
  list: PropTypes.arrayOf(PropTypes.any),
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  onChange: PropTypes.func,
  onChangePageSize: PropTypes.func,
  showPagination: PropTypes.bool,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  className: PropTypes.string,
  showSizeChanger: PropTypes.bool,
};

PaginateLight.defaultProps = {
  showSizeChanger: false,
  onChangePageSize: () => null,
};

export default PaginateLight;
